import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal, NgbModal, NgbNav, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { UserSettings } from 'app/global/user-settings.dto';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { delay, of, Subject, Subscription, take, takeUntil } from 'rxjs';
import uuid from 'short-unique-id';
import { ProfileService } from '../profile.service';
import { SignatureData, SignatureType } from './signature.dto';
import { SessionImageService } from 'app/global/session-image.service';

interface SignatureLoader {
  draw: boolean,
  type: boolean,
  upload: boolean,
  my_signature: boolean
}

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html',
  styleUrl: './signature.component.scss'
})

export class SignatureComponent implements OnDestroy, OnInit {
  
  
  @Input() profileData: UserSettings;
  @ViewChild('navWithIcons') navWithIcons: NgbNav;

  set setSignatureType(value: SignatureType) {
      this.signatureType = value || SignatureType.MYSIGNATURE;
      this.initSignaturePad();
  }

  @Input() get setSignatureType(): SignatureType {
    return this.signatureType;
  }

  constructor(
    private globalService: GlobalService,
    private profileService: ProfileService,
    private modalService: NgbModal,
    private activeModal: NgbActiveModal,
    private translocoService: TranslocoService,
    private _userSettingsFacade$: UserSettingsFacade,
    public sessionImageService: SessionImageService
  ) { }
  
  
  /** Signature section */
  public uniqueid = new uuid({ length: 6, dictionary: 'number' });
  public penColors: Array<{ color: string, colorname: string }> = [{ color: '#0061ef', colorname: 'blue' }, { color: '#000', colorname: 'black' }, { color: 'red', colorname: 'red' }];
  public penColor: string = '#0061ef';
  public penColorIndex: number = 0;
  public uuid = this.uniqueid();
  public signatureType: SignatureType = SignatureType.MYSIGNATURE;
  public signatureTypeEnum = SignatureType;
  public nextFont: number = 0;
  public fontFamily: string = 'Montserrat';
  public selectedSignatureFontSize: string = '34';
  public signatureImage: string = '';
  public imageChangedEvent: string = '';
  public croppedImage: string = '';
  public signatureSubmitted: boolean = false;
  public signatureValue: string = '';
  public signatureBG: string = '';
  public signatureloaders: SignatureLoader = { draw: false, type: false, upload: false, my_signature: false };
  public signatureSubcription: Subscription;
  public url_signature: string = '';
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this._userSettingsFacade$._userSettings$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (userSettings: UserSettings) => {
        this.profileData = userSettings;
        this.url_signature = this.sessionImageService.getSignatureImages();
      }
    });
  }


  /**
   * Change nav tab behalf on tabid
   * @param tabId 
   */
  changeTab(tabId: string) {
    this.navWithIcons.select(tabId);
  }


  applyBgImage() {
    this.signatureBG = this.croppedImage;
  }


  /**
   * Create or update new signature
   * @returns Object
   */
  submitSignatureForm(skipValidation: boolean = false) {
    let signature = '';

    if (this.signatureType === SignatureType.DRAW && !this.globalService.signatureObjects[this.uuid]._isEmpty) {
      signature = this.globalService.signatureObjects[this.uuid].toDataURL();
    } else if (this.signatureType === SignatureType.TYPE && this.signatureValue) {
      signature = this.generateSignature();
    } else if (this.signatureType === SignatureType.UPLOAD && this.imageChangedEvent) {
      signature = this.croppedImage ?? '';
    }

    if (!signature && skipValidation === false) return;

    this.signatureloaders[this.signatureType] = true;
    this.profileService.createOrUpdatesignature({ signature: signature }).subscribe({
      next: (response: SignatureData) => {
        this.signatureloaders[this.signatureType] = false;
        this.signatureSubmitted = false;
        this.globalService.toastrSuccess(this.translocoService.translate("Toaster message for the generate signature in profile (Profile signature section) - Signature has been updated"));
        this.url_signature = this.sessionImageService.getSignatureImages();
        this._userSettingsFacade$._userSettings$.pipe(take(1)).subscribe(user_settings => {
          const updatedUserSettings = {...user_settings, ...{ signature: response.signature }};
          this.globalService.updateUserSettingInState(updatedUserSettings);
        })
        this.clearSignature();
        this.changeTab('my_signature');
        this.activeModal.close({ status: 'done', signature: response });
      },

      error: (error) => {
        this.signatureSubmitted = false;
        this.signatureloaders[this.signatureType] = false;
        this.globalService.toastrError(error);
      }
    });
  }



  cropperReady() {}


  resetSignature() {
    this.signatureloaders.my_signature = true;
    this._userSettingsFacade$._userSettings$.pipe(take(1)).subscribe({
      next: (userSettings: UserSettings) => {
        const settings = {...userSettings};
        settings.signature = null;
        this.profileService.createOrUpdatesignature({signature: ''}).subscribe({
          next: () => {
            this.globalService.toastrSuccess(this.translocoService.translate("Toaster message for the reset signature in profile (Profile signature section) - Signature has been reset"));
            this.globalService.updateUserSettingInState(settings);
          },
          error: (error) => {
            this.signatureloaders.my_signature = false
            this.globalService.toastrError(error)
          },
          complete: () => this.signatureloaders.my_signature = false
        });
      }
    });
  }


  /** Signature model open form */
  uploadSignatureModalClick(uploadSignatureModal) {
    this.modalService.open(uploadSignatureModal, {
      size: 'lg' // size: 'xs' | 'sm' | 'lg' | 'xl'
    });
  }


  /** Generate canvas behalf on text */
  generateSignature() {
    const name = this.signatureValue;
    let fontSize = 120;
    if (name.length > 6) {
      fontSize = fontSize - (name.length * 1.2);
    }
    const canvas = <HTMLCanvasElement>(document.getElementById("CustomSignCanvas"));
    canvas.width = canvas.getBoundingClientRect().width * 2;
    canvas.height = (canvas.getBoundingClientRect().height < 350 ? 350 : canvas.getBoundingClientRect().height);//canvas.getBoundingClientRect().height;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const x = width / 2;
    const y = height / 2;
    ctx.clearRect(0, 0, width, height);
    ctx.font = fontSize + "pt " + this.fontFamily;
    ctx.fillStyle = String(this.penColor);
    ctx.textAlign = "center";
    ctx.textBaseline = 'middle';
    ctx.fillText(name, x, y);
    return canvas.toDataURL('image/png', 1);
  }


  /**
   * Change signature tab and patching event
   * @param $event 
   */
  changeSignatureTab($event: NgbNavChangeEvent) {
    this.signatureType = $event.nextId;
    this.signatureValue = '';
    if (this.signatureType === SignatureType.DRAW) {
      this.initSignaturePad();
    }
  }

  initSignaturePad() {
    this.signatureSubcription = of(null)
    .pipe(
      delay(300),
      take(1)
    )
    .subscribe(() => {
      this.globalService.initializeSignature(document.getElementById('regular_signaturepad'), this.uuid, [], this.penColor);
      this.globalService.signatureObjects[this.uuid].addEventListener("beginStroke", () => {
        this.signatureValue = 'Filled';
      });
    });
  }

  /**
   * Change color of signature pen
   * @param color 
   * @param index 
   */
  changeColor(color, index) {
    this.penColor = color.color;
    this.penColorIndex = index;
    if (this.signatureType === SignatureType.DRAW) {
      this.globalService.signatureObjects[this.uuid].penColor = color.color;
      let penData = this.globalService.signatureObjects[this.uuid].toData();
      penData = penData.map(d => {
        d.penColor = color.color;
        return d;
      });
      this.globalService.signatureObjects[this.uuid].fromData(penData, { clear: false });
    }
  }


  /** Clear signature */
  clearSignature() {
    if (this.signatureType === SignatureType.DRAW) {
      this.globalService.signatureObjects[this.uuid].clear();
    }
    this.signatureValue = '';
    this.signatureBG = '';
  }


  /** Change font family on signature */
  changeNextFont() {
    this.nextFont++;
    if (this.nextFont >= this.globalService.fonts.length) {
      this.nextFont = 0;
    }
    this.fontFamily = this.globalService.fonts[this.nextFont];
  }


  /**
   * Get signature image
   * @param $event 
   */
  getSignatureImage($event) {
    const maxSizeInBytes = 2.1 * 1024 * 1024;
    const fileSize = ($event.target as HTMLInputElement).files[0].size;
    if (fileSize >= maxSizeInBytes) {
      this.globalService.toastrError('Image size exceeds the maximum allowed (2MB)');
      return;
    }
    this.imageChangedEvent = $event;
    this.signatureImage = $event;

  }


  /** Remove signature image */
  removeSignatureImage() {
    this.imageChangedEvent = '';
    this.croppedImage = '';
    this.signatureImage = '';
    this.signatureValue = '';
    this.signatureBG = '';
  }


  /**
   * When image getting cropped then this function will triggered
   * @param event 
   */
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }


  /** Useless function we will use in future */
  imageLoaded() {
    this.signatureValue = 'Image';
  }


  ngOnDestroy() {
    if (this.signatureSubcription) {
      this.signatureSubcription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
